import type { ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InkBlock } from '@/components/atoms/InkBlock';
import { cn } from '@/lib/utils';
import { dwrClassesById } from '@/data/classes';
import type { Character } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import type { ClassId } from '@/schemas/classId';
import type { Move } from '@/schemas/move';
import { getEffectiveLevelForPick } from '@/services/multiclass';
import type { useExpandedState } from '@/hooks/useExpandedState';
import { MoveCard } from '@/components/molecules/MoveCard';

// page-specific (Fase 2 PR 9 — decisione B1): sub-componenti di
// `LevelUpStepMulticlass` estratti per rispettare `max-lines: 400` nel
// file principale. Pattern coerente con `stepRouting.ts` /
// `applyLevelUpHelpers.ts` di PR 8.

interface ClassSelectorProps {
  pool: readonly ClassId[];
  sourceClassId: ClassId | undefined;
  onChange: (sourceClassId: ClassId) => void;
}

export function ClassSelector({ pool, sourceClassId, onChange }: ClassSelectorProps) {
  return (
    <div className="space-y-1">
      <Label
        htmlFor="multiclass-class-select"
        className="font-display text-ink-soft text-[0.78rem] tracking-[0.08em] uppercase"
      >
        Classe-source
      </Label>
      <Select
        {...(sourceClassId !== undefined && { value: sourceClassId })}
        onValueChange={(v) => onChange(v as ClassId)}
      >
        <SelectTrigger id="multiclass-class-select">
          <SelectValue placeholder="Scegli classe" />
        </SelectTrigger>
        <SelectContent>
          {pool.map((c) => (
            <SelectItem key={c} value={c}>
              {classLabel(c)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

interface MoveListProps {
  character: Character;
  config: NonNullable<Move['multiclass']>;
  classDef: ClassDefinition;
  sourceClassId: ClassId;
  movePool: readonly Move[];
  selectedMoveId: string;
  expandState: ReturnType<typeof useExpandedState>;
  onSelect: (move: Move) => void;
  renderSetupSlot: (move: Move) => ReactNode;
}

export function MoveList(props: MoveListProps) {
  const {
    character,
    config,
    classDef,
    sourceClassId,
    movePool,
    selectedMoveId,
    expandState,
    onSelect,
    renderSetupSlot,
  } = props;
  const effectiveLevel = getEffectiveLevelForPick(character, sourceClassId, config, classDef);
  const { expanded, toggleExpanded } = expandState;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <InkBlock variant="full" className="flex-1">
          Mosse di {classLabel(sourceClassId)} (livello effettivo {effectiveLevel})
        </InkBlock>
        <ExpandAllToolbar hasMoves={movePool.length > 0} expandState={expandState} />
      </div>
      {movePool.length === 0 ? (
        <p className="text-ink-soft font-serif text-[0.92rem] italic">
          Nessuna mossa disponibile in {classLabel(sourceClassId)} a questo livello effettivo.
        </p>
      ) : (
        <RadioGroup
          value={selectedMoveId || ''}
          className="gap-2"
          aria-label={`Mosse multiclasse disponibili di ${classLabel(sourceClassId)}`}
        >
          {movePool.map((move) => {
            const inputId = `multiclass-move-${move.id}`;
            const isSelected = selectedMoveId === move.id;
            return (
              <MoveCard
                key={move.id}
                move={move}
                fallbackId={move.id}
                expanded={expanded.has(move.id)}
                onToggle={() => toggleExpanded(move.id)}
                labelHtmlFor={inputId}
                selected={isSelected}
                leading={
                  <RadioGroupItem
                    value={move.id}
                    id={inputId}
                    className="mt-1"
                    onClick={() => onSelect(move)}
                  />
                }
                trailing={<MoveTypeChip type={move.type} />}
                setupSlot={isSelected ? renderSetupSlot(move) : null}
              />
            );
          })}
        </RadioGroup>
      )}
    </div>
  );
}

function MoveTypeChip({ type }: { type: Move['type'] }) {
  return (
    <span
      className={cn(
        'font-display border-rule bg-paper text-ink-soft inline-flex items-center border px-[0.5em] py-[0.15em] text-[0.65rem] tracking-[0.06em] uppercase',
      )}
    >
      {moveTypeLabel(type)}
    </span>
  );
}

interface ExpandAllToolbarProps {
  hasMoves: boolean;
  expandState: ReturnType<typeof useExpandedState>;
}

function ExpandAllToolbar({ hasMoves, expandState }: ExpandAllToolbarProps) {
  if (!hasMoves) return null;
  const { allExpanded, expandAll, collapseAll } = expandState;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={allExpanded ? collapseAll : expandAll}
    >
      {allExpanded ? 'Comprimi tutte' : 'Espandi tutte'}
    </Button>
  );
}

function classLabel(classId: ClassId): string {
  const def = dwrClassesById[classId];
  return def?.name ?? classId;
}

function moveTypeLabel(type: Move['type']): string {
  switch (type) {
    case 'starting':
      return 'iniziale';
    case 'advanced_2_5':
      return 'avanzata L2-5';
    case 'advanced_6_10':
      return 'avanzata L6-10';
    default:
      return type;
  }
}
